import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Upload, X, ArrowRight, ArrowLeft, Send, MapPin, Layers, DollarSign, Target } from 'lucide-react';
import { CriteriaSelector } from '@/components/CriteriaSelector';
import { LocationSelector } from '@/components/LocationSelector';
import { calculateMatchScore } from '@/utils/criteria';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProposalForm = ({ ad, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [matchScore, setMatchScore] = useState(0);
  const [formData, setFormData] = useState({
    location_data: {
      address: '',
      coords: [43.5808, 7.1251],
      radius: 500
    },
    property_address: '',
    property_type: ad?.property_type || '',
    surface: ad?.min_surface ? Math.round((ad.min_surface + (ad.max_surface || ad.min_surface)) / 2) : '',
    rooms: ad?.rooms || '',
    price: '',
    description: '',
    criteria: {},
    personalized_message: '',
    visit_availability: ''
  });

  useEffect(() => {
    if (ad) {
      const proposalData = {
        property_type: formData.property_type,
        rooms: formData.rooms,
        surface: formData.surface,
        price: formData.price,
        criteria: formData.criteria
      };
      const score = calculateMatchScore(ad, proposalData);
      setMatchScore(score);
    }
  }, [formData.property_type, formData.rooms, formData.surface, formData.price, formData.criteria, ad]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    onDrop: (acceptedFiles) => setPhotos(prev => [...prev, ...acceptedFiles])
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('acquirer_ad_id', ad.ad_id);
      formDataToSend.append('property_address', formData.property_address);
      formDataToSend.append('location_data', JSON.stringify(formData.location_data));
      formDataToSend.append('property_type', formData.property_type);
      formDataToSend.append('surface', formData.surface);
      if (formData.rooms) formDataToSend.append('rooms', formData.rooms);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('description', formData.personalized_message);
      formDataToSend.append('personalized_message', formData.personalized_message);
      formDataToSend.append('match_score', matchScore.toString());
      formDataToSend.append('criteria', JSON.stringify(formData.criteria));
      if (formData.visit_availability) formDataToSend.append('visit_availability', formData.visit_availability);
      
      photos.forEach(photo => formDataToSend.append('photos', photo));

      await axios.post(`${API}/proposals`, formDataToSend, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Proposition envoyée !');
      onSuccess();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = () => {
    if (matchScore >= 80) return 'text-green-600 bg-green-50';
    if (matchScore >= 60) return 'text-yellow-600 bg-yellow-50';
    if (matchScore >= 40) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getScoreText = () => {
    if (matchScore >= 80) return '🎯 Excellent match !';
    if (matchScore >= 60) return '✓ Bon match';
    if (matchScore >= 40) return '⚠️ Match moyen';
    return '❌ Faible correspondance';
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-foreground">
            Proposer un bien
          </DialogTitle>
          <p className="font-body text-sm text-muted-foreground">Annonce de {ad?.title}</p>
        </DialogHeader>

        <div className={`p-4 rounded border-2 mb-4 ${
          matchScore >= 80 ? 'border-green-600 bg-green-50' :
          matchScore >= 60 ? 'border-yellow-600 bg-yellow-50' :
          matchScore >= 40 ? 'border-orange-600 bg-orange-50' :
          'border-red-600 bg-red-50'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold flex items-center gap-2">
                <Target className="w-4 h-4" />
                Score de compatibilité
              </p>
              <p className="text-xs opacity-75">Basé sur les critères cochés</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{matchScore}%</p>
              <p className="text-sm">{getScoreText()}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 my-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-2 flex-1 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-center text-sm text-gray-600 mb-4">Étape {step}/3</p>

        <div className="bg-blue-50 p-3 rounded-lg mb-4 text-sm">
          <p className="font-semibold text-blue-800 mb-1">💡 Recherche de l'acquéreur :</p>
          <div className="flex flex-wrap gap-2 text-xs">
            {ad?.property_type && <span className="bg-white px-2 py-1 rounded">{ad.property_type}</span>}
            {ad?.rooms && <span className="bg-white px-2 py-1 rounded">{ad.rooms} pièces</span>}
            {ad?.min_surface && <span className="bg-white px-2 py-1 rounded">{ad.min_surface}-{ad.max_surface || ad.min_surface}m²</span>}
            {ad?.max_budget && <span className="bg-white px-2 py-1 rounded">Max: {ad.max_budget.toLocaleString('fr-FR')}€</span>}
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-bold text-gray-800">📍 Localisation & Informations</h3>
            
            <div>
              <Label>Localisation du bien avec carte *</Label>
              <LocationSelector
                location={formData.location_data}
                showRadius={false}
                onChange={(locationData) => {
                  setFormData(prev => ({
                    ...prev,
                    location_data: locationData,
                    property_address: locationData.address
                  }));
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de pièces {ad?.rooms && `(recherché: ${ad.rooms})`}</Label>
                <Input
                  type="number"
                  value={formData.rooms}
                  onChange={(e) => setFormData(prev => ({...prev, rooms: e.target.value}))}
                  placeholder={ad?.rooms?.toString() || ''}
                />
              </div>
              <div>
                <Label>Surface (m²) * {ad?.min_surface && `(${ad.min_surface}-${ad.max_surface || ad.min_surface})`}</Label>
                <Input
                  type="number"
                  value={formData.surface}
                  onChange={(e) => setFormData(prev => ({...prev, surface: e.target.value}))}
                />
              </div>
            </div>

            <div>
              <Label>Prix (€) * {ad?.max_budget && `(max: ${ad.max_budget.toLocaleString('fr-FR')}€)`}</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-bold text-gray-800">🎯 Critères de votre bien</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cochez les critères correspondant à votre bien. Le score se calcule automatiquement.
            </p>
            <CriteriaSelector
              selectedCriteria={formData.criteria}
              onChange={(criteria) => setFormData(prev => ({...prev, criteria}))}
              propertyType={formData.property_type}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-bold text-gray-800">📸 Photos & Message</h3>
              <div
                {...getRootProps()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-600"
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-700 font-medium mb-2">Glissez vos photos ou cliquez</p>
                <p className="text-sm text-gray-500">JPG, PNG, WebP (minimum 1 photo)</p>
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {photos.map((photo, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Photo ${i + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

            <div className="mt-4">
              <Label>Message personnalisé *</Label>
              <Textarea
                rows={4}
                value={formData.personalized_message}
                onChange={(e) => setFormData(prev => ({...prev, personalized_message: e.target.value}))}
                placeholder="Pourquoi votre bien correspond à cette recherche..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-6 pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && (!formData.property_address || !formData.surface || !formData.price)}
              className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!formData.personalized_message || photos.length === 0 || loading}
              className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
            >
              {loading ? 'Envoi...' : `Envoyer (Match: ${matchScore}%)`}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalForm;
